import {
  FC,
  memo,
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip, { type ChipOwnProps } from "@mui/material/Chip";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import { useScoringStore, MAX_ACTIVE_PROFILES } from "@/store/scoringStore";
import {
  useCanManageScoring,
  MANAGE_SCORING_DISABLED_MESSAGE,
} from "@/hooks/useCanManageScoring";
import type { ScoringProfile } from "@/lib/scoring/types";

const CHIP_COLORS: ChipOwnProps["color"][] = ["success", "primary", "purple"];

interface ScoringPanelProps {
  onEditProfile?: (profile: ScoringProfile) => void;
  onAddProfile?: () => void;
}

export const ScoringPanel: FC<ScoringPanelProps> = memo(function ScoringPanel({
  onEditProfile,
  onAddProfile,
}) {
  const canManage = useCanManageScoring();
  const profiles = useScoringStore((s) => s.profiles);
  const activeProfileIds = useScoringStore((s) => s.activeProfileIds);
  const isLoading = useScoringStore((s) => s.isLoading);
  const error = useScoringStore((s) => s.error);
  const deactivateProfile = useScoringStore((s) => s.deactivateProfile);
  const setActiveProfileIds = useScoringStore((s) => s.setActiveProfileIds);

  const [tempSelectedIds, setTempSelectedIds] = useState<number[]>([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const scrollTopRef = useRef(0);

  // Restore scroll after MUI's internal scrollIntoView runs
  useEffect(() => {
    const saved = scrollTopRef.current;
    requestAnimationFrame(() => {
      if (listboxRef.current) {
        listboxRef.current.scrollTop = saved;
      }
    });
  }, [tempSelectedIds]);

  const activeProfiles = activeProfileIds
    .map((id) => profiles.find((p) => p.id === id))
    .filter((p): p is ScoringProfile => p !== undefined);

  const noActiveProfiles = activeProfiles.length === 0 && !isLoading;

  const tempSelected = useMemo(
    () =>
      tempSelectedIds
        .map((id) => profiles.find((p) => p.id === id))
        .filter((p): p is ScoringProfile => p !== undefined),
    [tempSelectedIds, profiles]
  );

  const handleOpen = useCallback(() => {
    setTempSelectedIds([...activeProfileIds]);
    setDropdownOpen(true);
  }, [activeProfileIds]);

  const handleClose = useCallback(() => {
    setDropdownOpen(false);
  }, []);

  const handleApply = useCallback(() => {
    setActiveProfileIds(tempSelectedIds);
    setDropdownOpen(false);
  }, [tempSelectedIds, setActiveProfileIds]);

  const handleToggleOption = useCallback(
    (_: unknown, value: ScoringProfile[]) => {
      if (value.length > MAX_ACTIVE_PROFILES) return;
      setTempSelectedIds(value.map((p) => p.id));
    },
    []
  );

  const handleDeactivate = useCallback(
    (id: number) => {
      deactivateProfile(id);
    },
    [deactivateProfile]
  );

  return (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography
            variant="subtitle2"
            fontWeight="bold"
            data-testid="scoring-panel-title"
          >
            SCORING
          </Typography>
          {isLoading && <CircularProgress size={12} />}
        </Stack>
        <Tooltip title={canManage ? "" : MANAGE_SCORING_DISABLED_MESSAGE}>
          {/* span keeps the tooltip working while the button is disabled */}
          <span>
            <Button
              size="small"
              disabled={!canManage}
              onClick={onAddProfile}
              startIcon={<AddIcon />}
              data-testid="scoring-new-profile-btn"
              sx={{
                py: 0.25,
                px: 1,
                fontSize: 12,
                minWidth: 0,
                color: "primary.main",
                fontWeight: 600,
              }}
            >
              New
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1, fontSize: 12 }}>
          {error}
        </Alert>
      )}

      {noActiveProfiles && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, fontSize: 12 }}
        >
          Apply a profile to rank companies.
        </Typography>
      )}

      {activeProfiles.length > 0 && (
        <Stack spacing={0.5} mt={1}>
          {activeProfiles.map((profile, index) => (
            <Chip
              key={profile.id}
              data-testid="scoring-active-profile-chip"
              deleteIcon={
                <span data-testid="scoring-chip-deactivate-icon">
                  <CancelIcon sx={{ fontSize: 18 }} />
                </span>
              }
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="inherit"
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {profile.name}
                  </Typography>
                  {canManage && (
                    <Box
                      component="button"
                      type="button"
                      aria-label={`Edit ${profile.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditProfile?.(profile);
                      }}
                      sx={{
                        cursor: "pointer",
                        background: "none",
                        border: "none",
                        p: 0,
                        display: "flex",
                        color: "inherit",
                        flexShrink: 0,
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 14 }} />
                    </Box>
                  )}
                </Box>
              }
              size="small"
              color={CHIP_COLORS[index]}
              onDelete={() => handleDeactivate(profile.id)}
              sx={{
                width: "100%",
                justifyContent: "flex-start",
                "& .MuiChip-label": { flex: 1, minWidth: 0, display: "flex" },
                "& .MuiChip-deleteIcon": {
                  display: "flex",
                  alignItems: "center",
                },
              }}
            />
          ))}
        </Stack>
      )}

      {profiles.length > 0 && (
        <>
          <Divider sx={{ my: 1 }} />
          <Autocomplete
            multiple
            open={dropdownOpen}
            onOpen={handleOpen}
            onClose={handleClose}
            disableCloseOnSelect
            autoHighlight={false}
            options={profiles}
            value={tempSelected}
            onChange={handleToggleOption}
            getOptionLabel={(p) => p.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            getOptionDisabled={(option) =>
              !tempSelectedIds.includes(option.id) &&
              tempSelectedIds.length >= MAX_ACTIVE_PROFILES
            }
            size="small"
            renderTags={() => null}
            renderInput={({ inputProps, ...params }) => (
              <TextField
                {...params}
                placeholder="Apply profile…"
                sx={{ fontSize: 13 }}
                slotProps={{
                  htmlInput: {
                    ...inputProps,
                    "data-testid": "scoring-apply-profile-select",
                  },
                }}
              />
            )}
            renderOption={(props, option) => {
              const isSelected = tempSelectedIds.includes(option.id);
              const selectionIndex = tempSelectedIds.indexOf(option.id);
              return (
                <li {...props} key={option.id}>
                  <Typography
                    variant="body2"
                    sx={{
                      flex: 1,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {option.name}
                  </Typography>
                  {isSelected && (
                    <Chip
                      label={selectionIndex + 1}
                      size="small"
                      color={CHIP_COLORS[selectionIndex]}
                      sx={{ ml: 1, minWidth: 24, height: 20 }}
                    />
                  )}
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditProfile?.(option);
                    }}
                    aria-label={`Edit ${option.name}`}
                    data-testid="scoring-edit-profile-btn"
                    sx={{ ml: 0.5 }}
                  >
                    <EditOutlinedIcon fontSize="small" />
                  </IconButton>
                </li>
              );
            }}
            slots={{
              paper: (paperProps) => (
                <Paper {...paperProps} onMouseDown={(e) => e.preventDefault()}>
                  {paperProps.children}
                  <Divider />
                  <Box
                    sx={{
                      p: 1,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleApply}
                      sx={{ fontWeight: 600 }}
                      data-testid="scoring-apply-profiles-btn"
                    >
                      APPLY
                    </Button>
                  </Box>
                </Paper>
              ),
            }}
            slotProps={{
              listbox: {
                ref: listboxRef,
                onScroll: (e: React.UIEvent<HTMLUListElement>) => {
                  scrollTopRef.current = e.currentTarget.scrollTop;
                },
                sx: { maxHeight: 312 },
              } as React.HTMLAttributes<HTMLUListElement>,
            }}
          />
        </>
      )}
    </Box>
  );
});
